import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { InputType } from 'config'
import { useEffect, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Checkbox, Section } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

const CONFIG_KEY = 'hmdaDefaultProperties'

type IHmdaDefaultPropertyField = InputType & { defaultValue?: any; defaultNA?: boolean; naDefaultable?: boolean }

interface IHmdaDefaultPropertiesResponse {
  orderedKeys: string[]
  fields: Record<string, IHmdaDefaultPropertyField>
}

export const HmdaDefaultPropertiesConfiguration = () => {
  const [loading, setLoading] = useState(false)
  const [orderedKeys, setOrderedKeys] = useState<string[]>([])
  const [fields, setFields] = useState<Record<string, IHmdaDefaultPropertyField>>({})
  const [isChanged, setIsChanged] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    setLoading(true)
    getAdminConfig(CONFIG_KEY)
      .then((res: IHmdaDefaultPropertiesResponse) => {
        setOrderedKeys(res?.orderedKeys || [])
        setFields(res?.fields || {})
      })
      .finally(() => setLoading(false))
  }, [])

  const onChangeValue = (fieldKey: string, value: any) => {
    setFields((prev) => {
      const next = cloneDeep(prev)
      if (!next[fieldKey]) return prev
      next[fieldKey].defaultValue = value
      return next
    })
    setIsChanged(true)
  }

  const onChangeNA = (fieldKey: string, value: boolean) => {
    setFields((prev) => {
      const next = cloneDeep(prev)
      if (!next[fieldKey]) return prev
      next[fieldKey].defaultNA = value
      if (value) next[fieldKey].defaultValue = ''
      return next
    })
    setIsChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    const payload: Record<string, { defaultValue?: any; defaultNA?: boolean }> = {}
    Object.keys(fields).forEach((key) => {
      payload[key] = { defaultValue: fields[key].defaultValue, defaultNA: !!fields[key].defaultNA }
    })
    await setAdminConfig(CONFIG_KEY, payload)
    toast('Saved successfully', { type: 'success' })
    setIsChanged(false)
    setLoading(false)
  }

  const renderField = (fieldKey: string) => {
    const field = fields[fieldKey]
    if (!field || field.inputType === 'section') return null

    const isNA = !!field.defaultNA

    const input: InputType = {
      ...field,
      value: isNA ? '' : field.defaultValue ?? '',
      readOnly: isNA,
      disabled: false,
      history: false,
    }

    if (field.naDefaultable) {
      input.additionalElements = (
        <Checkbox
          id={`${fieldKey}-defaultNA`}
          title="N/A"
          className="w-[unset]"
          value={isNA}
          onChange={(value) => onChangeNA(fieldKey, value)}
        />
      )
    }

    return (
      <div className={`col-span-${field.span || 1}`} key={fieldKey}>
        <RenderInput
          Key={fieldKey}
          input={input}
          onChange={(_key: string, value: any) => onChangeValue(fieldKey, value)}
        />
      </div>
    )
  }

  const renderContent = () =>
    orderedKeys.map((fieldKey) => {
      const field = fields[fieldKey]
      if (!field) return null

      if (field.inputType === 'section') {
        return (
          <div className="col-span-full" key={fieldKey}>
            <Section title={field.title || fieldKey} />
          </div>
        )
      }

      return renderField(fieldKey)
    })

  return (
    <div className="relative">
      <LayoutLoading show={loading} />
      <Prompt when={!!isChanged} message="Are you sure you want to leave without saving your changes?" />

      <p className="text-sm text-gray-500 mb-4">
        Set default values for HMDA fields. Defaults apply when a loan&apos;s HMDA record has no value for that field
        after system calculations.
      </p>

      <div className="grid gap-2 gap-y-4 md:grid-cols-4 sm:grid-cols-2 grid-cols-1">{renderContent()}</div>

      <SaveChanges show={!!isChanged} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
